import React, { useState, useEffect } from "react";
import { X, Save, RefreshCcw } from "lucide-react";
import { adminLifecycleService } from "../../../../services/api/settings";
import { LifecyclePepmTier } from "../../../../store/types/settings";

interface EditLifecycleTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: LifecyclePepmTier | null; // null means create mode
  onSuccess: () => void;
}

const EditLifecycleTierModal: React.FC<EditLifecycleTierModalProps> = ({
  isOpen,
  onClose,
  data,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    minEmployees: 0,
    maxEmployees: "" as number | string,
    pepmRate: 0,
    isCustom: false,
  });

  useEffect(() => {
    if (data) {
      setFormData({
        minEmployees: data.minEmployees || 0,
        maxEmployees: data.maxEmployees !== null && data.maxEmployees !== undefined ? data.maxEmployees : "",
        pepmRate: data.pepmRate || 0,
        isCustom: !!data.isCustom,
      });
    } else {
      setFormData({
        minEmployees: 0,
        maxEmployees: "",
        pepmRate: 0,
        isCustom: false,
      });
    }
  }, [data, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        minEmployees: Number(formData.minEmployees),
        maxEmployees: formData.maxEmployees === "" ? null : Number(formData.maxEmployees),
        pepmRate: formData.isCustom ? 0 : Number(formData.pepmRate),
        isCustom: formData.isCustom,
      };
      if (data) {
        await adminLifecycleService.pepmTiers.update(data.id, payload);
      } else {
        await adminLifecycleService.pepmTiers.create(payload);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Save tier failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {data ? "Edit PEPM Tier" : "Add PEPM Tier"}
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              {data ? "Update PEPM rate range and pricing" : "Create a new employee volume tier"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 flex flex-col">
          <div className="p-6 space-y-6 flex-1">
            <div className="space-y-4">
              <div className="flex items-end mb-4">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl w-full border border-transparent hover:border-primary/10 transition-all">
                  <input
                    type="checkbox"
                    checked={formData.isCustom}
                    onChange={(e) => setFormData({ ...formData, isCustom: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-bold text-gray-700 block">
                      Custom/Enterprise Tier
                    </span>
                    <span className="text-xs text-gray-400 font-medium">
                      If checked, pricing will require contact sales / custom negotiation.
                    </span>
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Min Employees
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.minEmployees}
                    onChange={(e) => setFormData({ ...formData, minEmployees: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Max Employees (leave empty for unlimited)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxEmployees}
                    onChange={(e) => setFormData({ ...formData, maxEmployees: e.target.value === "" ? "" : parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
              </div>

              {!formData.isCustom && (
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    PEPM Rate (USD per employee per month)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formData.pepmRate}
                    onChange={(e) => setFormData({ ...formData, pepmRate: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-8 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-bold disabled:opacity-50"
            >
              {loading ? (
                <RefreshCcw size={20} className="animate-spin" />
              ) : (
                <Save size={20} />
              )}
              <span>{loading ? "Saving..." : "Save Tier"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLifecycleTierModal;
